<script setup lang="ts">
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { CheckCircleIcon,ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'

type AlertModalType = "info" | "warning" | "success";

const emits = defineEmits(['close', 'cancel', 'confirm']);

const props = withDefaults(
  defineProps<{
    open: boolean,
    title: string,
    message?: string,
    confirmMessage: string,
    cancelMessage?: string | false,
    type: AlertModalType,
    width?: string,
  }>(),
  {
    width: "sm:max-w-lg"  
  }
);
</script>

<template>
  <TransitionRoot as="template" :show="open" :unmount="true">
    <Dialog as="div" class="fixed inset-0 z-10 overflow-y-auto" @close="emits('close')">
      <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div :class="[width]" class="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:w-full sm:p-6">
            <div class="sm:flex sm:items-start">
              <div v-if="props.type === 'warning'" class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationCircleIcon class="w-6 h-6 text-red-600" aria-hidden="true" />
              </div>
              <div v-else-if="props.type === 'info'" class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-blue-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                <InformationCircleIcon class="w-6 h-6 text-blue-600" aria-hidden="true" />
              </div>
              <div v-else-if="props.type === 'success'" class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-green-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                <CheckCircleIcon class="w-6 h-6 text-green-600" aria-hidden="true" />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <DialogTitle as="h3" :class="['text-lg font-medium leading-6 text-gray-900', !props.message ? 'mt-2' : '']">
                  {{ props.title }}
                </DialogTitle>
                <div v-if="props.message" class="mt-2">
                  <p class="text-sm text-gray-500">
                     <span v-html="message"></span>
                  </p>
                </div>
              </div>
            </div>
            <div class="pt-6 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <slot />
            </div>
            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button v-if="confirmMessage" type="submit" @click="emits('confirm')" class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
               :class="[
                (props.type === 'warning') ? 'hover:bg-red-700 bg-red-600 focus:ring-red-500' : '',
                (props.type === 'info') ? 'hover:bg-blue-700 bg-blue-600 focus:ring-blue-500' : '',
                (props.type === 'success') ? 'hover:bg-green-700 bg-green-600 focus:ring-green-500' : '',
               ]">
                {{ props.confirmMessage }}
              </button>
              <button v-if="cancelMessage !== false" type="button" @click="emits('cancel')" class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm">
                Annuler
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
